import { RootState } from '../../../../../../../../init-store';
import { DTMF_COMMANDS } from '../../../../../../../shared/features/room-agent/dtmf/dtmf-commands';
import { selectParticipantRenderState } from '../../../../../../../shared/features/room-agent/dtmf/participant-list/selectors';
import { i18n } from '../../../../../../../shared/helpers/i18n';
import { CONTAINER_BORDER_RADIUS, FONT_FAMILY, FOOTER_BACKGROUND_COLOR, TEXT_COLOR_PRIMARY } from '../constants';
import { drawDTMFChar, DTMFCharColor } from '../dtmf-char';
import { Area, Coordinates, Drawer } from '../types';
import { SCALE } from './constants';

export const CONTROLS_HEIGHT = Math.round(64 * SCALE);

const CONTROLS_FONT_SIZE = Math.round(20 * SCALE);
const CONTROLS_FONT = `${CONTROLS_FONT_SIZE}px ${FONT_FAMILY}`;
const CONTROL_ITEM_ICON_SIZE = Math.round(32 * SCALE);
const CONTROL_ITEM_ICON_MARGIN = Math.round(12 * SCALE);
const CONTROL_ITEM_MIDDLE_FONT_SIZE = Math.round(42 * SCALE);
const CLOSE_CHAR_OFFSET_Y = Math.round(-1 * SCALE);

export class ControlsDrawer implements Drawer {
    private fullArea: Area;
    private contentArea: Area;

    private leftControlCoordinates?: { icon: Coordinates; text: Coordinates };
    private middleControlCoordinates?: { icon: Coordinates; text: Coordinates };
    private rightControlCoordinates?: { icon: Coordinates; text: Coordinates };
    private textOffsetY?: number;
    private iconOffsetY = (CONTROLS_HEIGHT - CONTROL_ITEM_ICON_SIZE) / 2;

    constructor(fullArea: Area, contentArea: Area) {
        this.fullArea = fullArea;
        this.contentArea = contentArea;
    }

    private getTextOffsetY(ctx: CanvasRenderingContext2D) {
        if (this.textOffsetY === undefined) {
            ctx.textBaseline = 'top';
            ctx.font = CONTROLS_FONT;

            const textMetrics = ctx.measureText('T');
            const textHeight = textMetrics.actualBoundingBoxAscent + textMetrics.actualBoundingBoxDescent;

            this.textOffsetY = Math.round((CONTROLS_HEIGHT - textHeight) / 2);
        }

        return this.textOffsetY;
    }

    private getLeftControlCoordinates(ctx: CanvasRenderingContext2D) {
        if (!this.leftControlCoordinates) {
            const icon: Coordinates = {
                x: this.contentArea.x,
                y: this.contentArea.y + this.iconOffsetY,
            };
            const text: Coordinates = {
                x: icon.x + CONTROL_ITEM_ICON_SIZE + CONTROL_ITEM_ICON_MARGIN,
                y: this.contentArea.y + this.getTextOffsetY(ctx),
            };

            this.leftControlCoordinates = { icon, text };
        }

        return this.leftControlCoordinates;
    }

    private getMiddleControlCoordinates(ctx: CanvasRenderingContext2D, message: string) {
        if (!this.middleControlCoordinates) {
            ctx.textBaseline = 'top';
            ctx.font = CONTROLS_FONT;

            const textMetrics = ctx.measureText(message);
            const middleControlWidth = textMetrics.width + CONTROL_ITEM_ICON_MARGIN + CONTROL_ITEM_ICON_SIZE;

            const icon: Coordinates = {
                x: this.contentArea.x + (this.contentArea.width - middleControlWidth) / 2,
                y: this.contentArea.y + this.iconOffsetY,
            };

            const text: Coordinates = {
                x: icon.x + CONTROL_ITEM_ICON_MARGIN + CONTROL_ITEM_ICON_SIZE,
                y: this.contentArea.y + this.getTextOffsetY(ctx),
            };

            this.middleControlCoordinates = { icon, text };
        }

        return this.middleControlCoordinates;
    }

    private getRightControlCoordinates(ctx: CanvasRenderingContext2D, message: string) {
        if (!this.rightControlCoordinates) {
            const icon: Coordinates = {
                x: this.contentArea.x + this.contentArea.width - CONTROL_ITEM_ICON_SIZE,
                y: this.contentArea.y + this.iconOffsetY,
            };

            ctx.textBaseline = 'top';
            ctx.font = CONTROLS_FONT;

            const textMetrics = ctx.measureText(message);
            const text: Coordinates = {
                x: icon.x - CONTROL_ITEM_ICON_MARGIN - textMetrics.width,
                y: this.contentArea.y + this.getTextOffsetY(ctx),
            };

            this.rightControlCoordinates = { icon, text };
        }

        return this.rightControlCoordinates;
    }

    private drawFullArea(ctx: CanvasRenderingContext2D) {
        ctx.beginPath();
        ctx.fillStyle = FOOTER_BACKGROUND_COLOR;
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ctx.roundRect(this.fullArea.x, this.fullArea.y, this.fullArea.width, this.fullArea.height, [
            0,
            0,
            CONTAINER_BORDER_RADIUS,
            CONTAINER_BORDER_RADIUS,
        ]);
        ctx.fill();
    }

    private drawLeftControl(ctx: CanvasRenderingContext2D) {
        const coordinates = this.getLeftControlCoordinates(ctx);

        drawDTMFChar({
            ctx,
            x: coordinates.icon.x,
            y: coordinates.icon.y,
            char: DTMF_COMMANDS.participantList.prevPage,
            color: DTMFCharColor.default,
            size: CONTROL_ITEM_ICON_SIZE,
        });

        ctx.textBaseline = 'top';
        ctx.font = CONTROLS_FONT;
        ctx.fillStyle = TEXT_COLOR_PRIMARY;
        ctx.fillText(i18n('dtmf', 'participant-list.prev-page'), coordinates.text.x, coordinates.text.y);
    }

    private drawMiddleControl(ctx: CanvasRenderingContext2D) {
        const message = i18n('dtmf', 'common.close');

        // Сейчас переводы могут не успеть загрузиться. Возможно в будущем это уже исправлено
        if (!message) {
            return;
        }

        const coordinates = this.getMiddleControlCoordinates(ctx, message);

        drawDTMFChar({
            ctx,
            x: coordinates.icon.x,
            y: coordinates.icon.y,
            char: DTMF_COMMANDS.common.close,
            color: DTMFCharColor.accent,
            size: CONTROL_ITEM_ICON_SIZE,
            fontSize: CONTROL_ITEM_MIDDLE_FONT_SIZE,
            charOffsetY: CLOSE_CHAR_OFFSET_Y,
        });

        ctx.textBaseline = 'top';
        ctx.font = CONTROLS_FONT;
        ctx.fillStyle = TEXT_COLOR_PRIMARY;
        ctx.fillText(message, coordinates.text.x, coordinates.text.y);
    }

    private drawRightControl(ctx: CanvasRenderingContext2D) {
        const message = i18n('dtmf', 'participant-list.next-page');

        // Сейчас переводы могут не успеть загрузиться. Возможно в будущем это уже исправлено
        if (!message) {
            return;
        }

        const coordinates = this.getRightControlCoordinates(ctx, message);

        drawDTMFChar({
            ctx,
            x: coordinates.icon.x,
            y: coordinates.icon.y,
            char: DTMF_COMMANDS.participantList.nextPage,
            color: DTMFCharColor.default,
            size: CONTROL_ITEM_ICON_SIZE,
        });

        ctx.textBaseline = 'top';
        ctx.font = CONTROLS_FONT;
        ctx.fillStyle = TEXT_COLOR_PRIMARY;
        ctx.fillText(message, coordinates.text.x, coordinates.text.y);
    }

    public draw(ctx: CanvasRenderingContext2D, state: RootState) {
        const { canGoPrevPage, canGoNextPage } = selectParticipantRenderState(state);

        this.drawFullArea(ctx);

        if (canGoPrevPage) {
            this.drawLeftControl(ctx);
        }

        this.drawMiddleControl(ctx);

        if (canGoNextPage) {
            this.drawRightControl(ctx);
        }
    }
}
